/**
 * SABI — Header
 * Handles: desktop mega-menu open/close, mobile drawer with accordion
 * submenus, and the search flyout. Overlay/solid state is CSS-only (no JS).
 */
(function () {
  if (customElements.get('sabi-header')) return;

  const DESKTOP_QUERY = '(min-width: 990px)';
  const CLOSE_DELAY = 150;

  class SabiHeader extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.sabi-header__bar');
      this.navItems = Array.from(this.querySelectorAll('[data-sabi-nav-item]'));
      this.backdrop = this.querySelector('[data-sabi-backdrop]');
      this.drawer = this.querySelector('[data-sabi-drawer]');
      this.searchPanel = this.querySelector('[data-sabi-search-panel]');
      this.closeTimer = null;

      this._bindMegaMenu();
      this._bindDrawer();
      this._bindSearch();
      this._bindGlobalDismiss();
    }

    _bindMegaMenu() {
      this.navItems.forEach((item) => {
        if (!item.classList.contains('has-dropdown')) return;
        const trigger = item.querySelector('[data-sabi-nav-trigger]');
        const panel = item.querySelector('[data-sabi-mega]');
        if (!trigger || !panel) return;

        const open = () => {
          if (!window.matchMedia(DESKTOP_QUERY).matches) return;
          clearTimeout(this.closeTimer);
          this.navItems.forEach((other) => {
            if (other !== item) this._closeItem(other);
          });
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        };

        const scheduleClose = () => {
          clearTimeout(this.closeTimer);
          this.closeTimer = setTimeout(() => this._closeItem(item), CLOSE_DELAY);
        };

        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', scheduleClose);
        item.addEventListener('focusin', open);
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) scheduleClose();
        });

        trigger.addEventListener('click', (event) => {
          if (window.matchMedia(DESKTOP_QUERY).matches) return;
          event.preventDefault();
          const isOpen = item.classList.contains('is-open');
          this.navItems.forEach((other) => this._closeItem(other));
          if (!isOpen) {
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.navItems.forEach((item) => this._closeItem(item));
      });
    }

    _closeItem(item) {
      item.classList.remove('is-open');
      const trigger = item.querySelector('[data-sabi-nav-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    _bindDrawer() {
      const openBtn = this.querySelector('[data-sabi-drawer-open]');
      const closeBtn = this.querySelector('[data-sabi-drawer-close]');
      if (!openBtn || !this.drawer) return;

      const open = () => {
        this.drawer.hidden = false;
        this._showBackdrop();
        requestAnimationFrame(() => this.drawer.classList.add('is-open'));
        openBtn.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('sabi-scroll-lock');
        const firstLink = this.drawer.querySelector('a, button');
        if (firstLink) firstLink.focus({ preventScroll: true });
      };

      const close = () => {
        this.drawer.classList.remove('is-open');
        this._hideBackdrop();
        openBtn.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('sabi-scroll-lock');
        setTimeout(() => {
          if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
        }, 240);
        openBtn.focus({ preventScroll: true });
      };

      openBtn.addEventListener('click', open);
      if (closeBtn) closeBtn.addEventListener('click', close);
      this._closeDrawer = close;

      this.drawer.querySelectorAll('.sabi-header__drawer-toggle').forEach((toggle) => {
        toggle.addEventListener('click', () => {
          const row = toggle.closest('.sabi-header__drawer-row');
          const collapse = row && row.nextElementSibling;
          if (!collapse) return;
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!expanded));
          collapse.classList.toggle('is-open', !expanded);
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) close();
      });
    }

    _bindSearch() {
      const openBtns = this.querySelectorAll('[data-sabi-search-open]');
      const closeBtn = this.querySelector('[data-sabi-search-close]');
      if (!openBtns.length || !this.searchPanel) return;

      const open = () => {
        this.searchPanel.hidden = false;
        this._showBackdrop();
        const input = this.searchPanel.querySelector('[data-sabi-search-input]');
        if (input) input.focus({ preventScroll: true });
      };

      const close = () => {
        this.searchPanel.hidden = true;
        this._hideBackdrop();
      };

      openBtns.forEach((btn) => btn.addEventListener('click', open));
      if (closeBtn) closeBtn.addEventListener('click', close);
      this._closeSearch = close;

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.searchPanel.hidden) close();
      });
    }

    _showBackdrop() {
      if (!this.backdrop) return;
      this.backdrop.hidden = false;
      requestAnimationFrame(() => this.backdrop.classList.add('is-visible'));
      this.backdrop.addEventListener('click', this._onBackdropClick.bind(this), { once: true });
    }

    _hideBackdrop() {
      if (!this.backdrop) return;
      this.backdrop.classList.remove('is-visible');
      setTimeout(() => {
        if (!this.backdrop.classList.contains('is-visible')) this.backdrop.hidden = true;
      }, 200);
    }

    _onBackdropClick() {
      if (this.drawer && this.drawer.classList.contains('is-open') && this._closeDrawer) this._closeDrawer();
      if (this.searchPanel && !this.searchPanel.hidden && this._closeSearch) this._closeSearch();
    }

    _bindGlobalDismiss() {
      document.addEventListener('click', (event) => {
        if (this.contains(event.target)) return;
        this.navItems.forEach((item) => this._closeItem(item));
      });
    }
  }

  customElements.define('sabi-header', SabiHeader);
})();
